/**
 * Thin client for the BSO Public API used by the developer portal.
 *
 * All requests go to /v1/... through the shared API Gateway. The Cognito
 * ID token is attached as the Bearer credential, which the Lambda side
 * recognizes as an authenticated portal user.
 */

import { API_CONFIG } from '../../config/apiRouting';

const BASE = `${API_CONFIG.gateway.baseUrl.replace(/\/$/, '')}/v1`;

export interface PortalApiKey {
  keyId: string;
  enterpriseId: string;
  name: string;
  environment: 'production' | 'sandbox';
  scopes: string[];
  isActive: boolean;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

export interface PortalApiKeyCreated extends PortalApiKey {
  rawKey: string;
}

export interface PortalWebhookSubscription {
  id: string;
  url: string;
  events: string[];
  active: boolean;
  createdAt: string;
}

export interface PortalWebhookSubscriptionCreated extends PortalWebhookSubscription {
  signingSecret: string;
}

export type TokenGetter = () => Promise<string | null>;

async function request<T>(
  token: TokenGetter,
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const jwt = await token();
  if (!jwt) throw new Error('Not authenticated');

  const headers: Record<string, string> = {
    Accept: 'application/json',
    Authorization: `Bearer ${jwt}`,
    ...(options.body ? { 'Content-Type': 'application/json' } : {}),
    ...(options.method && !['GET', 'HEAD'].includes(options.method)
      ? { 'Idempotency-Key': crypto.randomUUID() }
      : {}),
    ...(options.headers as Record<string, string> | undefined)
  };

  const res = await fetch(`${BASE}${path}`, { ...options, headers });
  const text = await res.text();
  const parsed = text ? JSON.parse(text) : {};
  if (!res.ok) {
    const err = new Error(parsed?.error?.message || `Request failed: ${res.status}`);
    (err as any).code = parsed?.error?.code;
    (err as any).details = parsed?.error?.details;
    throw err;
  }
  return parsed.data as T;
}

export const portalApi = {
  listKeys: (token: TokenGetter) =>
    request<PortalApiKey[]>(token, '/api-keys'),

  createKey: (token: TokenGetter, payload: { name: string; environment: 'production' | 'sandbox'; scopes: string[] }) =>
    request<PortalApiKeyCreated>(token, '/api-keys', {
      method: 'POST',
      body: JSON.stringify(payload)
    }),

  revokeKey: (token: TokenGetter, keyId: string) =>
    request<PortalApiKey>(token, `/api-keys/${encodeURIComponent(keyId)}`, {
      method: 'DELETE'
    }),

  listWebhookSubscriptions: (token: TokenGetter) =>
    request<PortalWebhookSubscription[]>(token, '/webhooks/subscriptions'),

  createWebhookSubscription: (token: TokenGetter, payload: { url: string; events: string[] }) =>
    request<PortalWebhookSubscriptionCreated>(token, '/webhooks/subscriptions', {
      method: 'POST',
      body: JSON.stringify(payload)
    }),

  deleteWebhookSubscription: (token: TokenGetter, subscriptionId: string) =>
    request<{ id: string; deleted: boolean }>(token, `/webhooks/subscriptions/${encodeURIComponent(subscriptionId)}`, {
      method: 'DELETE'
    })
};
